import React from 'react';

import { Block } from '../common/Block.jsx';
import { Clickable, Hoverable, HoverableLink, Link, Wrapper as ButtonWrapper, withBlockTypeConfigPropsV2 } from '../common/Button.jsx';
import { withHoverColorChange } from '../common/Container.jsx';
import Defaults from '../layout/Defaults.jsx';
import { FormButton, withValidation } from '../common/Form.jsx';
import { default as _Image } from '../common/Image.jsx';
import Name from '../common/Name.jsx';
import PageType from '../common/PageType.jsx';
import { ContentContainer } from '../common/Popup.jsx';
import Utilities from '../common/Utilities.jsx';

const Framing = {
    framed: 'Framed',
    unframed: 'Unframed'
}
const Mode = {
    confirmation: 'confirmation',
    editionSelection: 'edition_selection'
}
function _Component(props) {
    // Selection state
    const [editionGroups, setEditionGroups] = React.useState({ groups: [] });

    const [selectedDimensions, setSelectedDimensions] = React.useState('');
    const updateSelectedDimensions = e => setSelectedDimensions(e.target.value);

    const [selectedMedium, setSelectedMedium] = React.useState('');
    const updateSelectedMedium = e => setSelectedMedium(e.target.value);

    const [selectedFraming, setSelectedFraming] = React.useState(Framing.unframed);
    const selectedFramingRef = React.useRef();
    selectedFramingRef.current = selectedFraming;
    const updateSelectedFraming = e => setSelectedFraming(e.target.value);

    const [selectedEditionId, setSelectedEditionId] = React.useState(undefined);
    const selectedEditionIdRef = React.useRef();
    selectedEditionIdRef.current = selectedEditionId;

    const [dimensions, setDimensions] = React.useState([]);
    const [editions, setEditions] = React.useState([]);
    const [media, setMedia] = React.useState([]);

    // Content state
    const findHasFramedEditions = (editionGroups) => editionGroups.groups.flatMap(x => x.editions).filter(x => x.priceFramed != null).length > 0;
    const findHasMultipleEditions = (editionGroups) => editionGroups.groups.flatMap(x => x.editions).length > 1;
    const hasFramedEditions = props.builder || findHasFramedEditions(editionGroups);
    const hasMultipleEditions = findHasMultipleEditions(editionGroups);

    // Framing availability
    const isFramingBlock = (block) => block.config && block.config.block && block.config.block.type == Name.Block.PurchasePopup.DropDown.Framing;
    const findIsFramingAvailable = (contentConfig) => contentConfig[Mode.editionSelection] && contentConfig[Mode.editionSelection].blocks && Block.findBlockConfig(isFramingBlock, contentConfig[Mode.editionSelection].blocks) != null

    // Design config
    let isFramingAvailable;
    let contentConfig = props.contentConfig;
    if (!contentConfig) {
        contentConfig = props.config && props.config.documents && props.config.documents[PageType.Popup.Purchase];
    }
    if (!contentConfig) {
        contentConfig = Defaults.PageConfig[PageType.Popup.Purchase](props.config);
        isFramingAvailable = true;
    } else {
        isFramingAvailable = findIsFramingAvailable(contentConfig);
    }

    const isEditionActiveOrUnlimited = (edition) => edition.active; // || edition.editionType == 2;
    React.useEffect(() => {
        let _dimensions = new Set();
        let _editions = [];
        let _media = new Set();
        editionGroups.groups.forEach(group => {
            let editionList = group.editions;
            if (selectedFraming == Framing.framed) {
                // Find editions in group with framed price
                editionList = editionList.filter(edition => isEditionActiveOrUnlimited(edition) && edition.priceFramed != null);
            }
            if (editionList.length == 0) {
                // No editions found => Don't include the group
                return;
            }

            if (group.medium) {
                _media.add(group.medium);

                // Find dimensions and editions for selected medium
                if (group.medium == selectedMedium) {
                    if (group.dimensions) {
                        _dimensions.add(group.dimensions);
                    }

                    // Find editions for selected dimensions
                    if (group.dimensions == selectedDimensions) {
                        editionList.forEach(edition => {
                            // Add active editions to the list
                            if (isEditionActiveOrUnlimited(edition)) {
                                _editions.push(edition);
                            } else if (edition.editionType == 2 && editionList.length == 1) {
                                _editions.push({ ...edition, active: true });
                            }
                        });
                    }
                }
            }
        });
        const dimensionsArray = Array.from(_dimensions);
        setDimensions(dimensionsArray);
        if (!_dimensions.has(selectedDimensions)) {
            // Previous selected dimensions no longer available => Select first in list
            setSelectedDimensions(dimensionsArray[0] || '');
        }
        const mediaArray = Array.from(_media);
        setMedia(mediaArray);
        if (!_media.has(selectedMedium)) {
            // Previous selected medium no longer available => Select first in list
            setSelectedMedium(mediaArray[0] || '');
        }
        setEditions(_editions);
        if (_editions.length) {
            setSelectedEditionId(_editions[0].editionId);
        }
    }, [selectedDimensions, selectedFraming, selectedMedium]);

    const handleDropDownBlockProps = (d, blockProps) => {
        if (!d.config || !d.config.block) {
            return {};
        }
        blockProps.visible = hasMultipleEditions;
        switch (d.config.block.type) {
            case Name.Block.PurchasePopup.DropDown.DimensionsV1:
            case Name.Block.PurchasePopup.DropDown.DimensionsV2:
                blockProps.data = dimensions || [];
                blockProps.value = selectedDimensions;
                blockProps.onChange = updateSelectedDimensions;
                break;
            case Name.Block.PurchasePopup.DropDown.Framing:
                blockProps.data = [];
                if (hasFramedEditions) {
                    blockProps.data = [Framing.unframed, Framing.framed];
                    if (!hasMultipleEditions && editionGroups.groups.length > 0 && editionGroups.groups[0].editions && editionGroups.groups[0].editions.length > 0) {
                        const temp = editionGroups.groups[0].editions[0];
                        blockProps.onOption = (x) => <option key={x} value={x}>{x} | {x == Framing.framed ? temp.priceFramed : temp.price}</option>
                    }
                    blockProps.value = selectedFraming;
                    blockProps.visible = props.builder || isFramingAvailable;
                    blockProps.onChange = updateSelectedFraming;
                } else {
                    blockProps.visible = false;
                }
                break;
            case Name.Block.PurchasePopup.DropDown.MediaV1:
            case Name.Block.PurchasePopup.DropDown.MediaV2:
                blockProps.data = media || [];
                blockProps.value = selectedMedium;
                blockProps.onChange = updateSelectedMedium;
                break;
            case Name.Block.PurchasePopup.DropDown.PriceV1:
            case Name.Block.PurchasePopup.DropDown.PriceV2:
                blockProps.data = editions.filter(x => isEditionActiveOrUnlimited(x));
                blockProps.value = selectedEditionId;
                blockProps.onChange = handleEditionSelect;
                blockProps.onOption = (x) => {
                    const price = selectedFraming == Framing.unframed ? x.price : x.priceFramed;
                    return (
                        <option key={x.editionId} value={x.editionId}>
                            {
                                x.editionType === 0
                                    ? `Original | ${price}`
                                    : x.editionType === 1
                                        ? `${x.number || 'Edition'} | ${price}`
                                        : x.number
                                            ? `${x.number} | ${price}`
                                            : price
                            }
                        </option>
                    )
                }
                break;
        }

        if (props.builder) {
            blockProps.disabled = props.mode == Mode.confirmation;
        } else {
            blockProps.disabled = props.disabled;
        }
    }
    const [loading, setLoading] = React.useState(!props.builder);
    React.useEffect(() => {
        if (props.builder) {
            return;
        }
        setLoading(true);
        $.get(
            '/api/editionSets',
            { editionSetId: props.inventory.id, sub: props.sub },
            (responseData) => {
                const hasFramedEditions = findHasFramedEditions(responseData);
                const hasMultipleEditions = findHasMultipleEditions(responseData);
                const isFramingAvailable = findIsFramingAvailable(contentConfig);
                if (hasMultipleEditions || (hasFramedEditions && isFramingAvailable)) {
                    setEditionGroups(previousState => {
                        return responseData;
                    });
                    setSelectedMedium(responseData.groups[0].medium);
                    setSelectedDimensions(responseData.groups[0].dimensions);
                    setSelectedEditionId(responseData.groups[0].editions[0].editionId);
                }
                else {
                    handleUpdateCartLink(responseData.groups[0].editions[0].editionId);
                }
                setLoading(false);
            }).fail(() => setLoading(false));

        if (window && window.gtag) {
            gtag('event', 'purchase-modal', {
                'title': props.inventory.title
            });
        }
    }, [props.inventory.id]);

    const [cartLink, setCartLink] = React.useState('');
    const handleUpdateCartLink = (editionId, framing) => {
        const localEditions = cart.getItemsFromStorage();
        if (editionId) {
            let found = false;
            for (let i = 0; i < localEditions.length; i++) {
                if (localEditions[i].EditionId != editionId) {
                    continue;
                }
                localEditions[i] = { EditionId: editionId, IsFramed: framing == Framing.framed, Quantity: 1 };
                found = true;
                break;
            }
            if (!found) {
                localEditions.push({ EditionId: editionId, IsFramed: framing == Framing.framed, Quantity: 1 })
            }
            cart.setItems(localEditions, props.querySub);
        }
        setCartLink(cart.formatUrl(localEditions, props.querySub));
    }

    const handleEditionSelect = (event) => {
        if (!event || !event.currentTarget) {
            return;
        }
        setSelectedEditionId(event.currentTarget.value);
    }

    const editionAdded = !hasMultipleEditions && (!hasFramedEditions || !isFramingAvailable) || props.formData.formState == 'success';
    const handleEditionSubmit = () => {
        if (!selectedEditionIdRef || !selectedEditionIdRef.current || props.formData.formState == 'success') {
            return;
        }
        handleUpdateCartLink(selectedEditionIdRef.current, selectedFramingRef.current);
        props.onSubmit();
    };
    const handleFormFieldProps = React.useCallback(({ type }, formFieldProps) => {
        switch (type) {
            case Name.Block.PurchasePopup.SubmitButtonV1:
            case Name.Block.PurchasePopup.SubmitButtonV2:
                formFieldProps.editor = props.editor;
                formFieldProps.formState = props.formData.formState;
                formFieldProps.inPopup = true;
                formFieldProps.onClick = handleEditionSubmit
                break;
        }
    }, [props.formData.errorMessage, props.formData.formState]);
    const handleBlockProps = (d) => {
        if (!d.type) {
            return;
        }
        let blockProps = { editor: props.editor };
        switch (d.type.value) {
            case Name.Block.PurchasePopup.CartButtonV1:
            case Name.Block.PurchasePopup.CartButtonV2:
                blockProps.href = cartLink;
                blockProps.onClick = () => props.onClose();
                break;
            case Name.Block.PurchasePopup.ContinueButtonV1:
            case Name.Block.PurchasePopup.ContinueButtonV2:
                blockProps.onClick = props.onClose;
                break;
            case Name.Block.PurchasePopup.DropDown.DimensionsV1:
            case Name.Block.PurchasePopup.DropDown.DimensionsV2:
            case Name.Block.PurchasePopup.DropDown.Framing:
            case Name.Block.PurchasePopup.DropDown.MediaV1:
            case Name.Block.PurchasePopup.DropDown.MediaV2:
            case Name.Block.PurchasePopup.DropDown.PriceV1:
            case Name.Block.PurchasePopup.DropDown.PriceV2:
                handleDropDownBlockProps(d, blockProps);
                break;
            case Name.Block.PurchasePopup.SubmitButtonV1:
            case Name.Block.PurchasePopup.SubmitButtonV2:
                blockProps.visible = props.builder || hasMultipleEditions || hasFramedEditions;
                blockProps.onFormFieldProps = handleFormFieldProps;
                break;
        }
        return blockProps;
    }
    let mode;
    if (props.builder) {
        mode = props.mode;
    } else if (!loading) {
        if (editionAdded) {
            mode = Mode.confirmation;
        } else {
            mode = Mode.editionSelection;
        }
    } else {
        return null;
    }
    return (
        <ContentContainer
            {...props}
            blockPropsHandler={handleBlockProps}
            className="ol--purchase"
            contentConfig={contentConfig}
            disabled={false}
            mode={mode}
            onFormFieldProps={handleFormFieldProps} />
    )
}
const Component = withValidation(_Component, []);

// #region CART BUTTON

function _CartButton(props) {
    const style = {}
    Utilities.copyObject(props.style, style);
    if (props.builder || props.disabled) {
        style.pointerEvents = 'none';
    }
    let className = 'btn btn--whitelabel btn--cta btn--custom2 richText btn--form--popup';
    if (props.className) {
        className += ` ${props.className}`;
    }
    let ButtonComponent = Link;
    if (props.hoverProps != undefined) {
        ButtonComponent = HoverableLink;
    }
    return (
        <ButtonWrapper {...props}>
            <ButtonComponent
                className={className}
                design={props.design}
                hoverProps={props.hoverProps}
                href={props.href}
                style={style}
                onClick={props.onClick} />
        </ButtonWrapper>
    );
}
const CartButtonV1 = withBlockTypeConfigPropsV2(_CartButton);

const CartButtonV2 = withBlockTypeConfigPropsV2(_CartButton, undefined, true);

// #endregion CART BUTTON

// #region CONTINUE BUTTON

function _ContinueButton(props) {
    const style = {}
    Utilities.copyObject(props.style, style);
    if (props.builder || props.disabled) {
        style.pointerEvents = 'none';
    }
    let className = 'btn btn--custom2 btn--whitelabel richText btn--form--popup';
    if (props.className) {
        className += ` ${props.className}`;
    }
    let ButtonComponent = Clickable;
    if (props.hoverProps != undefined) {
        ButtonComponent = Hoverable;
    }
    return (
        <ButtonWrapper {...props}>
            <ButtonComponent
                className={className}
                design={props.design}
                hoverProps={props.hoverProps}
                style={style}
                type="button"
                onClick={props.onClick} />
        </ButtonWrapper>
    );
}
const ContinueButtonV1 = withBlockTypeConfigPropsV2(_ContinueButton);

const ContinueButtonV2 = withBlockTypeConfigPropsV2(_ContinueButton, undefined, true);

// #endregion CONTINUE BUTTON

// #region DROP DOWN

function _DropDown(props) {
    if (!props.builder && props.hoverProps && props.hoverProps.design) {
        const hoverStyleProps = Utilities.styleFromString(Utilities.extractStyle(props.hoverProps.design));
        Utilities.copyObject(hoverStyleProps, props.hoverProps);
    }
    const style = {}
    let typeStyle = 'p';
    if (props.design) {
        typeStyle = Utilities.moveTokenStyle(props.design, style);
    }
    Utilities.copyObject(props.style, style);
    if (props.builder || props.disabled) {
        style.pointerEvents = 'none';
    }
    let className = `btn btn--custom2 btn--whitelabel richText btn--form btn--select ${typeStyle}`;
    if (props.className) {
        className += ` ${props.className}`;
    }
    let builderOption;
    if (props.builder) {
        let display = 'Sample Drop Down Value';
        if (props.blockConfig && props.blockConfig.block && props.blockConfig.block.type) {
            switch (props.blockConfig.block.type) {
                case Name.Block.PurchasePopup.DropDown.DimensionsV1:
                case Name.Block.PurchasePopup.DropDown.DimensionsV2:
                    display = 'Select Dimensions...';
                    break;
                case Name.Block.PurchasePopup.DropDown.Framing:
                    display = 'Select Framing...';
                    break;
                case Name.Block.PurchasePopup.DropDown.MediaV1:
                case Name.Block.PurchasePopup.DropDown.MediaV2:
                    display = 'Select Medium...';
                    break;
                case Name.Block.PurchasePopup.DropDown.PriceV1:
                case Name.Block.PurchasePopup.DropDown.PriceV2:
                    display = 'Select Price...';
                    break;
            }
        }
        builderOption = <option value="">{display}</option>
    } else if (props.visible == false) {
        return null;
    }
    return (
        <ButtonWrapper
            containerStyle={props.containerStyle}
            disabled={!props.builder && props.disabled}
            inLayout={props.inLayout}
            stateSelectionDropDown={props.stateSelectionDropDown}
            wrapperClassName={props.wrapperClassName}>
            <select
                className={className}
                disabled={props.disabled}
                style={style}
                value={props.value || ''}
                onChange={props.onChange}
                onFocus={props.onFocus}
                onMouseOver={props.onMouseOver}>
                {builderOption}
                {props.data && props.data.map(x => props.onOption ? props.onOption(x) : <option key={x} value={x}>{x}</option>)}
            </select>
        </ButtonWrapper>
    )
}
const DropDownV1 = withBlockTypeConfigPropsV2(withHoverColorChange(_DropDown));
const DropDownV2 = withBlockTypeConfigPropsV2(withHoverColorChange(_DropDown), undefined, true);

// #endregion DROP DOWN

function SubmitButtonV1(props) {
    if (props.visible == false) {
        return null;
    }
    return <FormButton.V1 {...props} />
}

function SubmitButtonV2(props) {
    if (props.visible == false) {
        return null;
    }
    return <FormButton.V2 {...props} />
}

export {
    CartButtonV2,
    Component,
    ContinueButtonV2,
    DropDownV2,
    Mode,
    SubmitButtonV2,

    // DEPRECATED
    CartButtonV1,
    ContinueButtonV1,
    DropDownV1,
    SubmitButtonV1
};
